import { useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Array, AsyncStateN, pipe } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import { InfoMessage } from '@root/shared/ui-kit'

import { LeaderboardRow } from './leaderboard-row'
import { LeaderboardRowSkeleton } from './leaderboard-row-skeleton'

import type { LeaderboardTableEntry } from '@root/features/leaderboard/model'
import type { Error } from '@root/shared'
import type { ChildrenProps } from '@root/shared/react'

const skeletonsArray = Array.range(0, 20)

type Props = ChildrenProps & {
  onRetry?: () => void
  items: AsyncStateN.AsyncStateN<
    Error.UnknownError,
    Array<LeaderboardTableEntry>
  >
}

export const LeaderboardTable: React.FC<Props> = ({ items, onRetry }) => {
  const { t } = useTranslation()
  const [user] = useAtom(userAtom)

  const count = useMemo(
    () =>
      pipe(
        items,
        AsyncStateN.map((it) => it.length),
        AsyncStateN.getOrElseW(() => '-'),
      ),
    [items],
  )

  const renderContent = () => {
    if (items.type === 'Error') {
      return (
        <InfoBlockContainer>
          <InfoMessage intent="error" onRetry={onRetry} />
        </InfoBlockContainer>
      )
    }
    if (items.type === 'Success') {
      return (
        <List data-vaul-no-drag>
          {items.value.map((it) => {
            const isCurrent = it.userId === user?.delugeId
            return (
              <LeaderboardRow
                key={it.userId}
                date={it.date}
                name={it.name}
                profit={it.amount}
                rank={it.rank}
                highlighted={isCurrent}
                currencyTicker={it.currencyTicker}
              />
            )
          })}
        </List>
      )
    }

    return skeletonsArray.map((it) => (
      <LeaderboardRowSkeleton rank={it} key={it} />
    ))
  }

  return (
    <Root>
      <Header>
        <HeaderCell>
          <Count>{count}</Count>
        </HeaderCell>
        <HeaderCell>{t('leaderboard.user_names')}</HeaderCell>
        <HeaderCell>{t('common.date')}</HeaderCell>
        <HeaderCell
          css={css`
            text-align: right;
          `}
        >
          {t('common.profit')}
        </HeaderCell>
      </Header>
      {renderContent()}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
`

const List = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding-block: 0 32px;
`

const Header = styled.div`
  display: grid;
  grid-template-columns: 20px minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr);
  grid-gap: 4px;
  align-items: center;
  padding-inline: 12px;

  border-bottom: 1px solid rgba(255 255 255 / 5%);
  padding-block: 8px;
`

const HeaderCell = styled.div`
  color: var(--text-9);
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`

const Count = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: var(--text-12);
`

const InfoBlockContainer = styled.div`
  display: grid;
  align-items: start;
  padding-top: 32px;
`
